import { useSyncExternalStore } from 'react';

/**
 * Captures the browser's `beforeinstallprompt` as early as possible. The event fires once, shortly after load; if a
 * lazily-loaded page registered its listener later it would be missed. This module is imported first thing in
 * `main.tsx` (before React mounts) and again from `features/pwa/nav.ts`.
 */
export type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const emit = () => {
  for (const l of listeners) l();
};

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // no Chrome mini-infobar: the install button below opens the prompt instead
    deferred = e as InstallPromptEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    emit();
  });
}

export const getInstallPrompt = (): InstallPromptEvent | null => deferred;

export function subscribeInstall(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The saved `beforeinstallprompt`, or null when the browser never fired it (iPhone, already installed, in-app). */
export const useInstallPrompt = () => useSyncExternalStore(subscribeInstall, getInstallPrompt, () => null);

/**
 * Opens the browser's own install dialog. Call it straight from the tap: `prompt()` is the first thing that runs,
 * before any `await`, because Chrome only accepts it inside the user gesture. The event works once, so it is dropped.
 * Resolves true when the person accepts.
 */
export async function promptInstall(): Promise<boolean> {
  const ev = deferred;
  if (!ev) return false;
  const shown = ev.prompt();
  deferred = null;
  emit();
  try {
    await shown;
    return (await ev.userChoice).outcome === 'accepted';
  } catch {
    return false;
  }
}

export function isStandalone(): boolean {
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return window.matchMedia('(display-mode: standalone)').matches || nav.standalone === true;
}

export function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

/**
 * iOS version as [major, minor], or null when the user agent does not say. Safari 26 freezes "iPhone OS 18_6" in the
 * user agent, so its real version is the `Version/26.x` token; Chrome / Edge on iPhone only have the `OS x_y` part.
 */
export function iosVersion(ua: string = window.navigator.userAgent): [number, number] | null {
  const m = /Version\/(\d+)(?:\.(\d+))?/.exec(ua) ?? /OS (\d+)_(\d+)/.exec(ua);
  return m ? [Number(m[1]), Number(m[2] ?? 0)] : null;
}

/** Web push on iPhone exists from iOS 16.4 (and only from the Home Screen app). Unknown version counts as fine. */
export const iosTooOldForPush = (v: [number, number] | null) =>
  !!v && (v[0] < 16 || (v[0] === 16 && v[1] < 4));

/** Phones and tablets (iPadOS reports itself as a Mac, so a touch Mac counts too). */
export function isMobile(): boolean {
  const ua = window.navigator.userAgent;
  return (
    /android|iphone|ipad|ipod|mobile/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1)
  );
}
